import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import {
  ArrowLeft,
  Search,
  User,
  UserPlus,
  Calendar,
  MapPin,
  Loader2,
  Check,
} from "lucide-react";
import { formatPrice, formatDate } from "@ckgo2/shared/utils";

interface Trip {
  id: string;
  title: string;
  slug: string;
  featuredImage?: string;
  durationDays?: number;
  tripDates?: TripDate[];
}

interface TripDate {
  id: string;
  startDate: string;
  endDate: string;
  price: number;
  capacity: number;
  bookedCount: number;
  status: string;
}

interface Customer {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  street?: string;
  city?: string;
  zip?: string;
}

interface Participant {
  name: string;
  birthDate?: string;
}

export default function NewOrderPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Form state
  const [step, setStep] = useState<"trip" | "customer" | "details" | "review">("trip");
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [selectedDate, setSelectedDate] = useState<TripDate | null>(null);
  const [customerMode, setCustomerMode] = useState<"search" | "new">("search");
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [tripSearch, setTripSearch] = useState("");

  // New customer form
  const [newCustomer, setNewCustomer] = useState({
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    street: "",
    city: "",
    zip: "",
  });

  // Order details
  const [adultsCount, setAdultsCount] = useState(1);
  const [childrenCount, setChildrenCount] = useState(0);
  const [participants, setParticipants] = useState<Participant[]>([{ name: "" }]);
  const [customerNotes, setCustomerNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [status, setStatus] = useState("confirmed");

  // Fetch trips
  const { data: tripsData, isLoading: tripsLoading } = useQuery({
    queryKey: ["trips-for-order"],
    queryFn: () => api.get<ApiResponse<Trip[]>>("/api/admin/trips"),
  });

  // Fetch selected trip details (includes dates)
  const { data: tripDetailsData, isLoading: tripDetailsLoading } = useQuery({
    queryKey: ["trip-details", selectedTrip?.id],
    queryFn: () =>
      api.get<ApiResponse<Trip & { dates: TripDate[] }>>(
        `/api/admin/trips/${selectedTrip?.id}`
      ),
    enabled: !!selectedTrip?.id,
  });

  // Fetch customers
  const { data: customersData, isLoading: customersLoading } = useQuery({
    queryKey: ["customers-for-order"],
    queryFn: () => api.get<ApiResponse<Customer[]>>("/api/admin/customers"),
  });

  const trips = tripsData?.data || [];
  const customers = customersData?.data || [];
  const tripDates = tripDetailsData?.data?.dates || [];

  // Filter trips
  const filteredTrips = trips.filter(
    (t) =>
      t.title.toLowerCase().includes(tripSearch.toLowerCase()) ||
      t.slug.toLowerCase().includes(tripSearch.toLowerCase())
  );

  // Filter customers
  const filteredCustomers = customers.filter(
    (c) =>
      c.email.toLowerCase().includes(customerSearch.toLowerCase()) ||
      c.firstName?.toLowerCase().includes(customerSearch.toLowerCase()) ||
      c.lastName?.toLowerCase().includes(customerSearch.toLowerCase()) ||
      c.phone?.includes(customerSearch)
  );

  // Update participants array when adults count changes
  useEffect(() => {
    const totalCount = adultsCount + childrenCount;
    if (participants.length < totalCount) {
      setParticipants([
        ...participants,
        ...Array(totalCount - participants.length).fill({ name: "" }),
      ]);
    } else if (participants.length > totalCount) {
      setParticipants(participants.slice(0, totalCount));
    }
  }, [adultsCount, childrenCount]);

  // Calculate total price
  const totalPrice = selectedDate
    ? selectedDate.price * (adultsCount + childrenCount * 0.5)
    : 0;
  const depositAmount = Math.round(totalPrice * 0.5);

  // Create customer mutation
  const createCustomerMutation = useMutation({
    mutationFn: (data: typeof newCustomer) =>
      api.post<ApiResponse<Customer>>("/api/admin/customers", data),
    onSuccess: (data) => {
      setSelectedCustomer(data.data);
      setCustomerMode("search");
      queryClient.invalidateQueries({ queryKey: ["customers-for-order"] });
    },
  });

  // Create order mutation
  const createOrderMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<ApiResponse<{ id: string }>>("/api/admin/orders", data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
      navigate(`/orders/${data.data.id}`);
    },
  });

  const handleCreateCustomer = async () => {
    if (!newCustomer.email || !newCustomer.firstName || !newCustomer.lastName) {
      return;
    }
    createCustomerMutation.mutate(newCustomer);
  };

  const handleSubmit = async () => {
    if (!selectedTrip || !selectedDate || !selectedCustomer) return;

    const orderData = {
      customerId: selectedCustomer.id,
      tripId: selectedTrip.id,
      tripDateId: selectedDate.id,
      status,
      participants: participants.filter((p) => p.name),
      adultsCount,
      childrenCount,
      totalPrice: Math.round(totalPrice),
      depositAmount,
      customerNotes,
      internalNotes,
      source: "phone",
    };

    createOrderMutation.mutate(orderData);
  };

  const canProceedToCustomer = selectedTrip && selectedDate && !tripDetailsLoading;
  const canProceedToDetails = selectedCustomer;
  const canProceedToReview = participants.some((p) => p.name);

  return (
    <div className="mx-auto max-w-4xl">
      {/* Header */}
      <div className="mb-6 flex items-center gap-4">
        <button onClick={() => navigate("/orders")} className="btn-ghost btn-sm">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Nová objednávka
          </h1>
          <p className="text-sm text-gray-500">Ruční vytvoření objednávky</p>
        </div>
      </div>

      {/* Progress steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {[
            { id: "trip", label: "Zájezd" },
            { id: "customer", label: "Zákazník" },
            { id: "details", label: "Detaily" },
            { id: "review", label: "Shrnutí" },
          ].map((s, index) => (
            <div key={s.id} className="flex items-center">
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium ${
                  step === s.id
                    ? "bg-primary-600 text-white"
                    : index <
                      ["trip", "customer", "details", "review"].indexOf(step)
                    ? "bg-success-500 text-white"
                    : "bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400"
                }`}
              >
                {index <
                ["trip", "customer", "details", "review"].indexOf(step) ? (
                  <Check className="h-4 w-4" />
                ) : (
                  index + 1
                )}
              </div>
              <span
                className={`ml-2 hidden text-sm sm:block ${
                  step === s.id
                    ? "font-medium text-gray-900 dark:text-white"
                    : "text-gray-500"
                }`}
              >
                {s.label}
              </span>
              {index < 3 && (
                <div className="mx-4 h-0.5 w-8 bg-gray-200 dark:bg-gray-700 sm:w-16" />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step content */}
      <div className="card">
        {/* Step 1: Select trip */}
        {step === "trip" && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Vyberte zájezd a termín
            </h2>

            {/* Trip search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Hledat zájezd..."
                value={tripSearch}
                onChange={(e) => setTripSearch(e.target.value)}
                className="input pl-10"
              />
            </div>

            {/* Trips list */}
            {tripsLoading ? (
              <div className="flex h-32 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
              </div>
            ) : (
              <div className="max-h-64 space-y-2 overflow-y-auto">
                {filteredTrips.map((trip) => (
                  <button
                    key={trip.id}
                    onClick={() => {
                      setSelectedTrip(trip);
                      setSelectedDate(null);
                    }}
                    className={`flex w-full items-center gap-4 rounded-lg border p-3 text-left transition-colors ${
                      selectedTrip?.id === trip.id
                        ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
                        : "border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
                    }`}
                  >
                    {trip.featuredImage && (
                      <img
                        src={trip.featuredImage}
                        alt={trip.title}
                        className="h-12 w-16 rounded object-cover"
                      />
                    )}
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-white">
                        {trip.title}
                      </p>
                      <p className="text-sm text-gray-500">
                        {trip.durationDays} dní
                      </p>
                    </div>
                    {selectedTrip?.id === trip.id && (
                      <Check className="h-5 w-5 text-primary-600" />
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Trip dates */}
            {selectedTrip && (
              <div className="space-y-3 border-t pt-4 dark:border-gray-700">
                <h3 className="font-medium text-gray-900 dark:text-white">
                  Dostupné termíny
                </h3>
                {tripDetailsLoading ? (
                  <div className="flex h-16 items-center justify-center">
                    <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
                  </div>
                ) : tripDates.length > 0 ? (
                  <div className="grid gap-2 sm:grid-cols-2">
                    {tripDates
                      .filter((d) => d.status !== "cancelled")
                      .map((date) => (
                        <button
                          key={date.id}
                          onClick={() => setSelectedDate(date)}
                          className={`flex items-center justify-between rounded-lg border p-3 text-left transition-colors ${
                            selectedDate?.id === date.id
                              ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
                              : "border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-900 dark:text-white">
                              {formatDate(date.startDate)}
                            </span>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-primary-600">
                              {formatPrice(date.price)}
                            </p>
                            <p className="text-xs text-gray-500">
                              {date.capacity - date.bookedCount} míst
                            </p>
                          </div>
                        </button>
                      ))}
                  </div>
                ) : (
                  <p className="text-gray-500">Žádné aktivní termíny</p>
                )}
              </div>
            )}

            <div className="flex justify-end pt-4">
              <button
                onClick={() => setStep("customer")}
                disabled={!canProceedToCustomer}
                className="btn-primary"
              >
                Pokračovat
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Select/create customer */}
        {step === "customer" && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Zákazník
            </h2>

            {/* Mode toggle */}
            <div className="flex gap-2">
              <button
                onClick={() => setCustomerMode("search")}
                className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  customerMode === "search"
                    ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300"
                }`}
              >
                <User className="h-4 w-4" />
                Existující zákazník
              </button>
              <button
                onClick={() => setCustomerMode("new")}
                className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                  customerMode === "new"
                    ? "bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300"
                }`}
              >
                <UserPlus className="h-4 w-4" />
                Nový zákazník
              </button>
            </div>

            {customerMode === "search" ? (
              <>
                {/* Customer search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Hledat podle jména, emailu nebo telefonu..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    className="input pl-10"
                  />
                </div>

                {/* Customers list */}
                {customersLoading ? (
                  <div className="flex h-32 items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
                  </div>
                ) : (
                  <div className="max-h-64 space-y-2 overflow-y-auto">
                    {filteredCustomers.slice(0, 10).map((customer) => (
                      <button
                        key={customer.id}
                        onClick={() => setSelectedCustomer(customer)}
                        className={`flex w-full items-center justify-between rounded-lg border p-3 text-left transition-colors ${
                          selectedCustomer?.id === customer.id
                            ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
                            : "border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
                        }`}
                      >
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {customer.firstName} {customer.lastName}
                          </p>
                          <p className="text-sm text-gray-500">
                            {customer.email}
                            {customer.phone && ` | ${customer.phone}`}
                          </p>
                        </div>
                        {selectedCustomer?.id === customer.id && (
                          <Check className="h-5 w-5 text-primary-600" />
                        )}
                      </button>
                    ))}
                    {filteredCustomers.length === 0 && customerSearch && (
                      <p className="py-4 text-center text-gray-500">
                        Zákazník nenalezen.{" "}
                        <button
                          onClick={() => {
                            setCustomerMode("new");
                            setNewCustomer((prev) => ({
                              ...prev,
                              email: customerSearch.includes("@")
                                ? customerSearch
                                : "",
                            }));
                          }}
                          className="text-primary-600 hover:underline"
                        >
                          Vytvořit nového
                        </button>
                      </p>
                    )}
                  </div>
                )}
              </>
            ) : (
              /* New customer form */
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Jméno *
                  </label>
                  <input
                    type="text"
                    value={newCustomer.firstName}
                    onChange={(e) =>
                      setNewCustomer({ ...newCustomer, firstName: e.target.value })
                    }
                    className="input"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Příjmení *
                  </label>
                  <input
                    type="text"
                    value={newCustomer.lastName}
                    onChange={(e) =>
                      setNewCustomer({ ...newCustomer, lastName: e.target.value })
                    }
                    className="input"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Email *
                  </label>
                  <input
                    type="email"
                    value={newCustomer.email}
                    onChange={(e) =>
                      setNewCustomer({ ...newCustomer, email: e.target.value })
                    }
                    className="input"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Telefon
                  </label>
                  <input
                    type="tel"
                    value={newCustomer.phone}
                    onChange={(e) =>
                      setNewCustomer({ ...newCustomer, phone: e.target.value })
                    }
                    className="input"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Ulice
                  </label>
                  <input
                    type="text"
                    value={newCustomer.street}
                    onChange={(e) =>
                      setNewCustomer({ ...newCustomer, street: e.target.value })
                    }
                    className="input"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Město
                  </label>
                  <input
                    type="text"
                    value={newCustomer.city}
                    onChange={(e) =>
                      setNewCustomer({ ...newCustomer, city: e.target.value })
                    }
                    className="input"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    PSČ
                  </label>
                  <input
                    type="text"
                    value={newCustomer.zip}
                    onChange={(e) =>
                      setNewCustomer({ ...newCustomer, zip: e.target.value })
                    }
                    className="input"
                  />
                </div>
                <div className="sm:col-span-2">
                  <button
                    onClick={handleCreateCustomer}
                    disabled={
                      !newCustomer.email ||
                      !newCustomer.firstName ||
                      !newCustomer.lastName ||
                      createCustomerMutation.isPending
                    }
                    className="btn-primary"
                  >
                    {createCustomerMutation.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <UserPlus className="mr-2 h-4 w-4" />
                    )}
                    Vytvořit zákazníka
                  </button>
                </div>
              </div>
            )}

            {/* Selected customer info */}
            {selectedCustomer && (
              <div className="rounded-lg bg-success-50 p-4 dark:bg-success-900/20">
                <p className="font-medium text-success-700 dark:text-success-400">
                  Vybraný zákazník: {selectedCustomer.firstName}{" "}
                  {selectedCustomer.lastName} ({selectedCustomer.email})
                </p>
              </div>
            )}

            <div className="flex justify-between pt-4">
              <button onClick={() => setStep("trip")} className="btn-ghost">
                Zpět
              </button>
              <button
                onClick={() => setStep("details")}
                disabled={!canProceedToDetails}
                className="btn-primary"
              >
                Pokračovat
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Order details */}
        {step === "details" && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Detaily objednávky
            </h2>

            {/* Participants count */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Počet dospělých
                </label>
                <input
                  type="number"
                  min={1}
                  max={10}
                  value={adultsCount}
                  onChange={(e) => setAdultsCount(parseInt(e.target.value) || 1)}
                  className="input"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Počet dětí (do 12 let)
                </label>
                <input
                  type="number"
                  min={0}
                  max={10}
                  value={childrenCount}
                  onChange={(e) => setChildrenCount(parseInt(e.target.value) || 0)}
                  className="input"
                />
              </div>
            </div>

            {/* Participants */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Účastníci
              </label>
              <div className="space-y-2">
                {participants.map((p, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="w-8 text-sm text-gray-500">
                      {index + 1}.
                    </span>
                    <input
                      type="text"
                      placeholder={`Jméno účastníka ${index + 1}`}
                      value={p.name}
                      onChange={(e) => {
                        const updated = [...participants];
                        updated[index] = { ...p, name: e.target.value };
                        setParticipants(updated);
                      }}
                      className="input flex-1"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Počáteční stav objednávky
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="input"
              >
                <option value="inquiry">Poptávka</option>
                <option value="quoted">Naceneno</option>
                <option value="confirmed">Potvrzeno</option>
                <option value="deposit_paid">Záloha zaplacena</option>
                <option value="fully_paid">Plně zaplaceno</option>
              </select>
            </div>

            {/* Notes */}
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Poznámka zákazníka
              </label>
              <textarea
                rows={2}
                value={customerNotes}
                onChange={(e) => setCustomerNotes(e.target.value)}
                placeholder="Speciální požadavky, preference..."
                className="input"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Interní poznámka
              </label>
              <textarea
                rows={2}
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
                placeholder="Poznámky pro interní potřebu..."
                className="input"
              />
            </div>

            <div className="flex justify-between pt-4">
              <button onClick={() => setStep("customer")} className="btn-ghost">
                Zpět
              </button>
              <button
                onClick={() => setStep("review")}
                disabled={!canProceedToReview}
                className="btn-primary"
              >
                Pokračovat
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Review */}
        {step === "review" && (
          <div className="space-y-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Shrnutí objednávky
            </h2>

            {/* Trip info */}
            <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
              <div className="flex items-start gap-4">
                {selectedTrip?.featuredImage && (
                  <img
                    src={selectedTrip.featuredImage}
                    alt={selectedTrip.title}
                    className="h-20 w-28 rounded-lg object-cover"
                  />
                )}
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {selectedTrip?.title}
                  </h3>
                  <div className="mt-1 flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {selectedDate && formatDate(selectedDate.startDate)}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {selectedTrip?.durationDays} dní
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Customer info */}
            <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
              <h4 className="mb-2 font-medium text-gray-900 dark:text-white">
                Zákazník
              </h4>
              <p className="text-gray-600 dark:text-gray-300">
                {selectedCustomer?.firstName} {selectedCustomer?.lastName}
              </p>
              <p className="text-sm text-gray-500">{selectedCustomer?.email}</p>
              {selectedCustomer?.phone && (
                <p className="text-sm text-gray-500">{selectedCustomer.phone}</p>
              )}
            </div>

            {/* Participants */}
            <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
              <h4 className="mb-2 font-medium text-gray-900 dark:text-white">
                Účastníci ({adultsCount} dospělí
                {childrenCount > 0 && `, ${childrenCount} děti`})
              </h4>
              <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
                {participants
                  .filter((p) => p.name)
                  .map((p, i) => (
                    <li key={i}>{p.name}</li>
                  ))}
              </ul>
            </div>

            {/* Pricing */}
            <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
              <h4 className="mb-2 font-medium text-gray-900 dark:text-white">
                Cena
              </h4>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">
                    {adultsCount}x dospělý @ {formatPrice(selectedDate?.price || 0)}
                  </span>
                  <span className="text-gray-900 dark:text-white">
                    {formatPrice((selectedDate?.price || 0) * adultsCount)}
                  </span>
                </div>
                {childrenCount > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">
                      {childrenCount}x dítě (50%) @{" "}
                      {formatPrice((selectedDate?.price || 0) * 0.5)}
                    </span>
                    <span className="text-gray-900 dark:text-white">
                      {formatPrice((selectedDate?.price || 0) * 0.5 * childrenCount)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between border-t pt-2 dark:border-gray-600">
                  <span className="font-semibold text-gray-900 dark:text-white">
                    Celkem
                  </span>
                  <span className="font-bold text-primary-600">
                    {formatPrice(totalPrice)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Záloha (50%)</span>
                  <span className="text-gray-600 dark:text-gray-300">
                    {formatPrice(depositAmount)}
                  </span>
                </div>
              </div>
            </div>

            {/* Notes */}
            {(customerNotes || internalNotes) && (
              <div className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                <h4 className="mb-2 font-medium text-gray-900 dark:text-white">
                  Poznámky
                </h4>
                {customerNotes && (
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    <strong>Zákazník:</strong> {customerNotes}
                  </p>
                )}
                {internalNotes && (
                  <p className="mt-1 text-sm text-gray-500">
                    <strong>Interní:</strong> {internalNotes}
                  </p>
                )}
              </div>
            )}

            <div className="flex justify-between pt-4">
              <button onClick={() => setStep("details")} className="btn-ghost">
                Zpět
              </button>
              <button
                onClick={handleSubmit}
                disabled={createOrderMutation.isPending}
                className="btn-primary"
              >
                {createOrderMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Vytvářím...
                  </>
                ) : (
                  "Vytvořit objednávku"
                )}
              </button>
            </div>

            {createOrderMutation.isError && (
              <div className="rounded-lg bg-red-50 p-4 text-red-700 dark:bg-red-900/30 dark:text-red-400">
                Nepodařilo se vytvořit objednávku. Zkuste to prosím znovu.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
